import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ButtonModule,
  ConfirmationService,
  ConfirmDialogModule,
  SharedModule,
  TableModule,
  TooltipModule,
} from 'primeng';
import { IInspectionJob } from '../../../interfaces/inspections/inspection-job';
import { VmePipesModule } from '../../../vme-pipes.module';
import { Subscription } from 'rxjs';
import { BroadcastHubEventService } from '../../../services/broadcast-hub-event.service';
import { BroadcastHubService } from '../../../services/broadcast-hub.service';
import { ISubscription } from 'rxjs-compat/Subscription';
import { BroadcastHubMessage } from '../../../interfaces/broadcast-hub-message';
import { BroadcastHubMessageTypeEnum } from '../../../enums/broadcast-hub-message-group.enum';
import { Router } from '@angular/router';
import { InspectionDeploymentService } from '../../../services/inspection-deployment.service';
import {
  InspectionDeploymentRowStatus,
  InspectionDeploymentStatus,
} from '../../../enums/inspection-deployment-status.enum';
import { VehicleRegComponent } from '../../vehicle-reg/vehicle-reg.component';

@Component({
  selector: 'inspection-deployment',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    SharedModule,
    TableModule,
    VmePipesModule,
    TooltipModule,
    ConfirmDialogModule,
    VehicleRegComponent,
  ],
  templateUrl: './inspection-deployment.component.html',
  styleUrls: ['./inspection-deployment.component.scss'],
})
export class InspectionDeploymentComponent implements OnInit, OnDestroy {
  inspectionJobs: IInspectionJob[] = [];
  isLoaded = false;
  isDeployJobsButtonDisabled = true;
  deploymentStatus: InspectionDeploymentStatus = InspectionDeploymentStatus.AwaitingStart;
  constructor(
    private inspectionDeploymentService: InspectionDeploymentService,
    private broadcastHubEventService: BroadcastHubEventService,
    private broadcastHub: BroadcastHubService,
    private router: Router,
    private confirmationService: ConfirmationService,
  ) {}

  protected async deployJobs() {
    this.isDeployJobsButtonDisabled = true;
    this.setStatusOfAllRows(InspectionDeploymentRowStatus.InProgress);
    await this.inspectionDeploymentService.deployInspectionJobs();
  }
  private setStatusOfAllRows(status: InspectionDeploymentRowStatus) {
    this.deploymentStatus = InspectionDeploymentStatus.InProgress;
    this.inspectionJobs.forEach((x) => (x.deploymentStatus = status));
  }

  private eventSubscription: Subscription;
  busySubscription: ISubscription;

  async ngOnInit(): Promise<void> {
    await this.loadInspectionJobs();
    await this.loadDeploymentStatus();

    void this.broadcastHub.subscribeToChannel(`InspectionsDeployment`);

    this.eventSubscription = this.broadcastHubEventService.DeployInspectionsEvent.subscribe(
      (event) => this.handleDeployInspectionsEvent(event),
    );
  }

  private async loadDeploymentStatus() {
    // If all the rows are New, then deployment status is Awaiting start, otherwise, fetch it from the backend cache.
    if (this.inspectionJobs.every((x) => x.deploymentStatus == InspectionDeploymentRowStatus.New)) {
      this.deploymentStatus = InspectionDeploymentStatus.AwaitingStart;
    } else {
      this.deploymentStatus =
        await this.inspectionDeploymentService.getInspectionDeploymentStatus();
    }

    if (this.inspectionJobs.length > 0) {
      this.isDeployJobsButtonDisabled =
        this.deploymentStatus !== InspectionDeploymentStatus.AwaitingStart;
    }
  }

  private handleDeployInspectionsEvent(event: BroadcastHubMessage<any>) {
    console.log(
      'handleDeployInspectionsEvent: ' + event.messageType + ' ' + event.messageData + '',
    );
    const data = event.messageData;

    if (event.messageType === BroadcastHubMessageTypeEnum.DeployInspectionRowStatusChange) {
      const jobIndex = this.inspectionJobs.findIndex((x) => x.jobId == data.jobId);

      if (jobIndex !== -1) {
        this.inspectionJobs[jobIndex].deploymentStatus = data.status;
        this.inspectionJobs[jobIndex].errorMessage = data.message;
      }
    }

    if (event.messageType === BroadcastHubMessageTypeEnum.DeployInspectionStatusChange) {
      this.deploymentStatus = data.status;
    }
  }

  ngOnDestroy(): void {
    this.eventSubscription?.unsubscribe();
    if (this.busySubscription) this.busySubscription.unsubscribe();
  }

  async loadInspectionJobs() {
    this.isLoaded = false;
    this.inspectionJobs = await this.inspectionDeploymentService.getInspectionSiteJobs();
    this.isLoaded = true;
  }

  getRowStatusIcon(status: InspectionDeploymentRowStatus): { icon: string; color: string } {
    switch (status) {
      case InspectionDeploymentRowStatus.New:
        return {
          icon: 'fa fa-check-circle-o',
          color: '#6c757d',
        };
      case InspectionDeploymentRowStatus.InProgress:
        return {
          icon: 'fa fa-circle-o-notch fa-spin',
          color: '#17a2b8',
        };
      case InspectionDeploymentRowStatus.Deployed:
        return {
          icon: 'fa fa-check-circle',
          color: '#28a745',
        };

      case InspectionDeploymentRowStatus.Error:
        return {
          icon: 'fa fa-times-circle',
          color: '#dc3545',
        };

      default:
        return {
          icon: 'fa fa-question-circle',
          color: '#6c757d',
        };
    }
  }

  getRowStatusDescription(status: InspectionDeploymentRowStatus) {
    switch (status) {
      case InspectionDeploymentRowStatus.New:
        return 'New';
      case InspectionDeploymentRowStatus.InProgress:
        return 'In Progress';
      case InspectionDeploymentRowStatus.Deployed:
        return 'Deployed';

      case InspectionDeploymentRowStatus.Error:
        return 'Error';

      default:
        return 'Error';
    }
  }

  viewJob(jobId: number, event: MouseEvent): void {
    const url = `/app/jobscreen/${jobId}`;

    if (event?.ctrlKey || event?.metaKey) {
      window.open(url, '_blank');
    } else {
      void this.router.navigate([url]);
    }
  }

  getDeploymentStatusDescription(deploymentStatus: InspectionDeploymentStatus) {
    switch (deploymentStatus) {
      case InspectionDeploymentStatus.AwaitingStart:
        return 'Awaiting Start';
      case InspectionDeploymentStatus.InProgress:
        return 'In Progress';
      case InspectionDeploymentStatus.Deployed:
        return 'Deployed';
      default:
        return 'Error';
    }
  }

  getDeploymentStatusIcon() {
    switch (this.deploymentStatus) {
      case InspectionDeploymentStatus.AwaitingStart:
        return {
          icon: 'fa fa-check-circle-o',
          color: '#6c757d',
        };
      case InspectionDeploymentStatus.InProgress:
        return {
          icon: 'fa fa-circle-o-notch fa-spin',
          color: '#17a2b8',
        };
      case InspectionDeploymentStatus.Deployed:
        return {
          icon: 'fa fa-check-circle',
          color: '#28a745',
        };
    }
  }

  confirm() {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to deploy the jobs?',
      accept: () => {
        this.deployJobs().then();
      },
    });
  }
}
